/** 0 = không có hợp đồng (standalone), 1 = có hợp đồng */
export type LoaiPhieu = 0 | 1;

/** 1 = giá cố định (loai_hop_dong=1), 2 = giá linh hoạt (loai_hop_dong=2) */
export type LoaiHopDong = 1 | 2;

export type ProcessingStatus = 'pending' | 'processing' | 'success' | 'failed';

export interface ApiSuccessResponse<T> {
  status: number;
  data: T;
  message?: string | null;
}

export interface PhieuNhapKhoFormFields {
  id_hop_dong: number;
  id_kho: number;
  ma_tk_co?: string | null;
  ma_ct_co?: string | null;
  ten_tk_co?: string | null;
  nguoi_giao?: string | null;
  so_hop_dong?: string | null;
  ngay_hop_dong?: string | null;
  so_hoa_don?: string | null;
  ngay_hoa_don?: string | null;
}

export interface BienBanKiemNghiemFormFields {
  ngay_bien_ban: string;
  thanh_phan_hoi_dong?: string | null;
  y_kien?: string | null;
  ten_giam_doc?: string | null;
  mau_so?: string;
}

export interface PhieuNhapKhoChiTietInput {
  id_vat_tu: number;
  so_luong: number;
  don_gia: number;
  phuong_thuc_kiem_nghiem?: string | null;
  ket_qua_kiem_nghiem?: 0 | 1;
}

/** Raw line before normalization — accepts `id`/`so_tien` UI aliases */
export interface PhieuNhapKhoVatTuLineInput {
  id_vat_tu?: number;
  id?: number;
  so_luong: number;
  don_gia?: number;
  so_tien?: number;
  phuong_thuc_kiem_nghiem?: string | null;
  ket_qua_kiem_nghiem?: 0 | 1;
}

export function normalizePhieuNhapKhoChiTiet(
  line: PhieuNhapKhoVatTuLineInput
): PhieuNhapKhoChiTietInput | null {
  const id_vat_tu = line.id_vat_tu ?? line.id;
  const don_gia = line.don_gia ?? line.so_tien;

  if (!id_vat_tu || !don_gia || line.so_luong <= 0) {
    return null;
  }

  const item: PhieuNhapKhoChiTietInput = {
    id_vat_tu,
    so_luong: line.so_luong,
    don_gia,
  };

  if (line.phuong_thuc_kiem_nghiem) {
    item.phuong_thuc_kiem_nghiem = line.phuong_thuc_kiem_nghiem;
  }

  if (line.ket_qua_kiem_nghiem !== undefined) {
    item.ket_qua_kiem_nghiem = line.ket_qua_kiem_nghiem;
  }

  return item;
}

/** POST /phieu-nhap-kho/tao-moi — unified create (all modes) */
export interface PhieuNhapKhoCreateRequest {
  phieuNhap: PhieuNhapKhoFormFields;
  bienBanKiemNghiem: BienBanKiemNghiemFormFields;
  chiTiet: PhieuNhapKhoChiTietInput[];
}

/** POST /phieu-nhap-kho/cap-nhat/{id} — unified update */
export interface PhieuNhapKhoUpdateRequest {
  phieuNhap?: Partial<Omit<PhieuNhapKhoFormFields, 'id_kho' | 'id_hop_dong'>>;
  bienBanKiemNghiem?: Partial<BienBanKiemNghiemFormFields>;
  /** Full replace of line items via sync(). Omit to update headers only. */
  chiTiet?: PhieuNhapKhoChiTietInput[];
}

export interface HopDongVatTuSelectionQuery {
  s?: string;
  exclude_id_phieu_nhap?: number[];
}

export interface HopDongVatTuSelectionRow {
  id: number;
  id_vat_tu?: number;
  ma_vat_tu: string;
  ten_vat_tu: string;
  don_vi_tinh: string;
  is_valid: boolean;
  so_luong_hd: number;
  don_gia_hd: number;
  so_luong_da_nhap: number;
  so_luong_con_lai: number;
  tong_tien_hop_dong?: number;
  so_luong_hop_dong?: number;
  so_luong_nhap?: number;
  so_luong_thieu?: number;
  don_gia?: number;
}

export type HopDongVatTuSelectionResponse = ApiSuccessResponse<
  HopDongVatTuSelectionRow[]
>;

export interface PhieuNhapKhoApproveRequest {
  extra: {
    ngay_hoan_thanh: string;
  };
}

/** Labels for virtual loai_phieu filter / display (derived from id_hop_dong). */
export const LOAI_PHIEU_LABELS: Record<LoaiPhieu, string> = {
  0: 'Không có hợp đồng',
  1: 'Theo hợp đồng',
};

export const PROCESSING_STATUS_LABELS: Record<ProcessingStatus, string> = {
  pending: 'Chờ xử lý',
  processing: 'Đang xử lý',
  success: 'Hoàn thành',
  failed: 'Thất bại',
};

/**
 * GET /api/v1/phieu-nhap-kho/danh-sach
 * Results are DonVi + year scoped via request headers (`iddonvi`, `year`).
 * Omit empty filters — do not send null / "".
 */
export interface PhieuNhapKhoListQuery {
  /** Keyword search: so_phieu, so_hop_dong, so_hoa_don, ten_tk_co, id */
  s?: string;
  /** Filter by warehouse */
  id_kho?: number;
  /** 0 = standalone (id_hop_dong=0), 1 = contract-linked (id_hop_dong>0) */
  loai_phieu?: LoaiPhieu;
  processing_status?: ProcessingStatus;
  /** Laravel pagination page (default 1) */
  page?: number;
}

